import { ChannelkartNetworkGet, ChannelkartNetworkPost, ChannelkartNetworkPut } from "./networkService";
import { EnvironmentRegistry } from "../services/registry/environmentRegistry";

const url = EnvironmentRegistry.getServerUrl();
const size=1000;

export const fetchAllDataFromApi = async (url: string, size: number, pageKey: string = "page"): Promise<any[]> => {
  let allData: any[] = [];
  let page = 0;
  try {
    while (true) {
      const response = await ChannelkartNetworkGet(`${url}&size=${size}&${pageKey}=${page}`);
      const data = response?.data?.features ?? response?.data?.content ?? response?.data ?? [];
      allData.push(...data);
      if (data.length < size) {
        break;
      }
      page++;
    }
  } catch (error) {
    console.error("Error fetching data from the API", error);
  }
  return allData;
};

export const fetchAllDataFromApiPost = async (
  url: string,
  requestBody: any,
  size: number = 1000,
  pageKey: string = "page"
): Promise<any[]> => {
  let allData: any[] = [];
  let page = 0;
  
  try {
    while (true) {
      const paginatedUrl = `${url}${url.includes('?') ? '&' : '?'}size=${size}&${pageKey}=${page}`;
      
      const response = await ChannelkartNetworkPost(paginatedUrl, requestBody);
      
      const data = response?.data?.features ?? response?.data?.content ?? response?.data ?? [];
      allData.push(...data);
      
      if (data.length < size) {
        break;
      }
      page++;
    }
  } catch (error) {
    console.error("Error fetching data from the API via POST", error);
  }
  
  return allData;
};

export async function getClaims(
  startDate?: string,
  endDate?: string,
  claimSubType?: string,
  supplier: boolean = true,
  hierarchyMatch: boolean = false
) {
  const loginId = JSON.parse(localStorage.authContext)?.user?.loginId;
  const baseURL = EnvironmentRegistry.getClaimBaseUrl();

  let url = claimSubType
    ? `${baseURL}/v1/claims/subType/${claimSubType}`
    : `${baseURL}/v1/claims`;

  const params = new URLSearchParams();

  if (supplier) {
    params.append('supplier', loginId);
  }

  if (hierarchyMatch) {
    params.append('hierarchyMatch', hierarchyMatch.toString());
  }

  if (startDate && endDate) {
    params.append('startDate', startDate);
    params.append('endDate', endDate);
  }

  const paramString = params.toString();
  if (paramString) {
    url += `?${paramString}`;
  }
  const response = await fetchAllDataFromApi(url, size);
  return response;
}

export async function getClaimDetailsByReference(requestBody: any[]) {
  const response = await fetchAllDataFromApiPost(
    `${EnvironmentRegistry.getClaimBaseUrl()}/v1/claims/getDetailsByReference`,
    requestBody,
    1000,
    "page"
  );
  return response;
}

export async function getClaimDetails(claimId: string) {
    const response = await ChannelkartNetworkGet(
      `${EnvironmentRegistry.getClaimBaseUrl()}/v1/claims/${claimId}`,
      null
    );
    return response;
  }

export async function getClaimApprovalDetails(claimId: string) {
  const response = await ChannelkartNetworkGet(
     `${url}/approval/${claimId}`,
    null
  );
  return response;
}

export async function postClaimApprovalDetails(postBody:object,requestId: string) {
    const response = await ChannelkartNetworkPost(
     `${url}/approval/approve/${requestId}`,
     postBody
    );
    return response;
  }  

  export async function postClaim(postBody: object, approval: boolean = true) {
    const response = await ChannelkartNetworkPost(
      `${EnvironmentRegistry.getClaimBaseUrl()}/v1/claims?approval=${approval}`,
      postBody
    );
    return response;
  }

  export async function getStockData(type: string = "unclaimed") {
    const loginId = JSON.parse(localStorage.authContext)?.user?.loginId;
    const response = await fetchAllDataFromApi(
      `${EnvironmentRegistry.getStockBaseUrl()}/consolidatedStock/getStockReasonInfo?supplier=${loginId}&type=${type}`, size, "index");
    return response;
  }

  export async function updateClaimedStock(postBody: object) {
    const response = await ChannelkartNetworkPut(
      `${EnvironmentRegistry.getStockBaseUrl()}/consolidatedStock/claims`,
      postBody
    );
    return response;
  }

  export async function updateBlockedStockForClaims(postBody: object) {
    const response = await ChannelkartNetworkPut(
      `${EnvironmentRegistry.getStockBaseUrl()}/consolidatedStock/updateBlockedStockForClaims`,
      postBody
    );
    return response;
  }

  export async function getReturnsData(startDate: string, endDate: string) {
    const loginId = JSON.parse(localStorage.authContext)?.user?.loginId;
    const response = await fetchAllDataFromApi(
      `${url}/v3/sales/dmsInvoice?includeSalesDetails=true&sort=creationTime:desc&filter=creationTime[gte]:${startDate} and creationTime[lte]:${endDate} and type:return and supplier:${loginId}`,
      size
    );
    return response;
  }

  export async function getAvailableCreditForMultiple(returnIds: string) {
    const response = await fetchAllDataFromApi(
      `${url}/creditNote/getAvailableCreditForMultiple?returnIds=${returnIds}`,
      size
    );
    return response;
  }

  export async function getPtd(
    skuCode: string,
    pricingApiConfig: { basePath: string; queryParams: Record<string, string> }
  ) {
    const authContext = JSON.parse(localStorage.authContext || "{}");
    const loginId = authContext?.user?.loginId;
    const { basePath, queryParams } = pricingApiConfig;

    const queryString = Object.entries(queryParams)
      .map(([key, value]) => {
        const processedValue = value
          .replace("{skuCode}", encodeURIComponent(skuCode))
          .replace("{loginId}", encodeURIComponent(loginId));
        return `${key}=${processedValue}`;
      })
      .join("&");

    const apiUrl = `${url}${basePath}?${queryString}`;
    const response = await ChannelkartNetworkGet(apiUrl);

    return response.data.features;
  }

  export async function getExclTovInvoices(startDate?: string, endDate?: string) {
    const loginId = JSON.parse(localStorage.authContext)?.user?.loginId;
    const response = await ChannelkartNetworkGet(`${url}/v3/sales/exclTovInvoices?minimalResponse=true&startDate=${startDate}&endDate=${endDate}&supplier=${loginId}`, null);
    return response.data.features;
  }

  export async function getTovInvoices(startDate?: string, endDate?: string) {
    const loginId = JSON.parse(localStorage.authContext)?.user?.loginId;
    const response = await fetchAllDataFromApi(`${url}/v3/sales/tovInvoices?minimalResponse=true&startDate=${startDate}&endDate=${endDate}&supplier=${loginId}`, size);
    return response;
  }